import { Text, TextSkeleton } from "@solomon-labs/design/components/ui";
import React from "react";

import { BumpAnimation } from "../../components/Brand/BumpAnimation";
import { useCountUp } from "../../hooks/useCountUp";

interface YaasEarningsProjectionCardProps {
    /** Target yearly earnings in USD (raw number, not pre-formatted).
     *  The card runs a count-up from 0 to this value when
     *  `countEnabled` flips true. */
    yearlyValue: number;
    /** When true, the count-up runs from 0 to `yearlyValue` over
     *  600ms. Until then, the displayed value sits at 0. Typically
     *  flipped by a parent's mount-time choreography (e.g.
     *  `useStakeFlowEntrance`). */
    countEnabled: boolean;
    /** Triggers the receiver-card bump (translateY pulse). Typically
     *  flipped at the same moment `countEnabled` flips so the card
     *  visually "lands" with the count-up. */
    bumpTriggered: boolean;
    /** The projection's inputs did not load. The figure shows the
     *  loading skeleton rather than a number: a failed input must never
     *  read as "$0.00 per year", a fact the page does not know. The
     *  failure itself is reported once, on the balance card. */
    loading?: boolean;
}

/**
 * Earnings projection card — the "Earns you $X per year" surface
 * that pairs with a position preview in YaasEarnProjectionCTA.
 *
 * Owns its own count-up (via `useCountUp`) and bump (via
 * `<BumpAnimation>`). Caller drives both via `countEnabled` and
 * `bumpTriggered` — typically threaded from `useStakeFlowEntrance`.
 *
 * Mechanism-only / SDK-free. Hoisted into `@solomon-apps/shared`
 * (widgets/yaas); uses shared's own `useCountUp` (swapped off the
 * apps/main `@/hooks/useCountUp` on the lift). apps/main re-exports it
 * from its old path via a shim.
 */
export function YaasEarningsProjectionCard({
    yearlyValue,
    countEnabled,
    bumpTriggered,
    loading = false
}: YaasEarningsProjectionCardProps): React.ReactElement {
    const yearlyDisplay = useCountUp(yearlyValue, countEnabled, 600);
    return (
        <BumpAnimation
            triggered={bumpTriggered}
            className="border md:border-2 border-line-base p-3 md:p-6 flex flex-col gap-2"
        >
            <Text
                variant="small"
                render={<span />}
                className="text-foreground-base"
            >
                Earns you
            </Text>
            {loading ? (
                <TextSkeleton variant="number-lg" className="w-40" />
            ) : (
                <div className="flex items-baseline gap-2">
                    <Text
                        variant="number-lg"
                        className="text-success tabular-nums"
                    >
                        ${yearlyDisplay}
                    </Text>
                    <Text
                        variant="fine-print"
                        render={<span />}
                        className="text-foreground-muted"
                    >
                        per year
                    </Text>
                </div>
            )}
        </BumpAnimation>
    );
}
